import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { StarRating } from '../design-system';
import { useAuth } from '../../hooks/useAuth';
import { answerText, averageHostRating, fillForHost, type FeedbackItem } from '../../lib/hostFeedback';

/** What travelers chose to share about their trip with this host (Past trips). */
export function TravelerFeedback() {
  const { profile } = useAuth();
  const [items, setItems] = useState<FeedbackItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const hostFirst = profile?.full_name?.split(' ')[0] ?? null;

  useEffect(() => {
    void (async () => {
      const { data, error } = await supabase.rpc('host_trip_feedback');
      if (error) { setFailed(true); setItems([]); return; }
      setItems((data ?? []) as FeedbackItem[]);
    })();
  }, []);

  if (items === null) return null;
  if (failed) return <p className="text-xs text-app-muted">Couldn’t load traveler feedback right now.</p>;

  const { average, count } = averageHostRating(items);

  return (
    <section aria-labelledby="traveler-feedback" className="mb-10">
      <h2 id="traveler-feedback" className="font-serif text-xl text-app-fg mb-1">Traveler feedback</h2>
      <p className="text-xs text-app-muted mb-4">Ratings and comments travelers chose to share with you. Nothing about the app or price.</p>

      {items.length === 0 ? (
        <p className="text-sm text-app-muted">Nothing shared yet. Feedback appears here after a trip, when the traveler agrees to share it.</p>
      ) : (
        <>
          {average !== null && (
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-app-border bg-app-card px-4 py-3">
              <span className="font-serif text-[length:calc(26px*var(--text-scale))] leading-none text-app-fg">{average.toFixed(1)}</span>
              <div>
                <StarRating value={Math.round(average)} />
                <div className="text-caption text-app-muted">average of {count} rating{count === 1 ? '' : 's'}</div>
              </div>
            </div>
          )}
          <ul className="flex flex-col gap-3">
            {items.map((it) => (
              <li key={it.survey_id} className="rounded-xl border border-app-border bg-app-card px-4 py-3.5">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-serif text-callout text-app-fg">{it.trip_name ?? 'Trip'}</div>
                  <div className="shrink-0 text-caption text-app-muted">
                    {it.traveler_first_name ? `${it.traveler_first_name} · ` : ''}{new Date(it.completed_at).toLocaleDateString()}
                  </div>
                </div>
                <dl className="mt-2.5 flex flex-col gap-2">
                  {it.answers.map((a, i) => {
                    const label = fillForHost(a.label, hostFirst, it.trip_name);
                    if (a.kind === 'stars' && typeof a.value === 'number') {
                      return (
                        <div key={i} className="flex items-center justify-between gap-3">
                          <dt className="text-footnote text-app-fg-2">{label}</dt>
                          <dd><StarRating value={a.value} /></dd>
                        </div>
                      );
                    }
                    const text = answerText(a);
                    if (!text) return null;
                    return (
                      <div key={i}>
                        <dt className="text-caption text-app-muted">{label}</dt>
                        <dd className="text-footnote text-app-fg [text-wrap:pretty]">{a.kind === 'text' ? `“${text}”` : text}</dd>
                      </div>
                    );
                  })}
                </dl>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export default TravelerFeedback;
